import { create } from 'zustand';
import type { DailyBonusClaimResult, DailyBonusState } from '@tg/shared';
import { endpoints } from '@/api/endpoints';
import { useSession } from '@/stores/session';

/**
 * Guest / offline preview of the calendar: what a new account really starts with — day 1, no streak
 * (not a made-up streak, owner decision №12). Rewards = the server calendar.
 */
export const PREVIEW_DAILY: DailyBonusState = {
  day: 1,
  claimableNow: true,
  rewards: [100, 128, 168, 188, 288, 388, 888],
  streak: 0,
  nextClaimAt: null,
};

interface DailyStore {
  state: DailyBonusState;
  /** true once loaded from the server for the current user */
  live: boolean;
  claiming: boolean;
  load(): Promise<void>;
  claim(): Promise<DailyBonusClaimResult>;
  reset(): void;
}

export const useDailyBonus = create<DailyStore>()((set, get) => ({
  state: PREVIEW_DAILY,
  live: false,
  claiming: false,
  async load() {
    if (useSession.getState().status !== 'user') {
      set({ state: PREVIEW_DAILY, live: false });
      return;
    }
    try {
      const state = await endpoints.dailyBonus();
      set({ state, live: true });
    } catch {
      /* keep what we have */
    }
  },
  async claim() {
    if (get().claiming) throw new Error('busy');
    set({ claiming: true });
    try {
      const r = await endpoints.claimDailyBonus();
      set({ state: r.state, live: true });
      return r;
    } finally {
      set({ claiming: false });
    }
  },
  reset: () => set({ state: PREVIEW_DAILY, live: false, claiming: false }),
}));
